import {
  Card, CardContent, CardHeader,
  CircularProgress,
  Divider, IconButton, List,
  ListItem, ListItemAvatar, ListItemSecondaryAction, ListItemText,
  Tooltip, Typography
} from '@material-ui/core';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import Alert from '@material-ui/lab/Alert';
import { makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import axios from "src/utils/axios";
import { getUser, openBlob, safeRequestParam } from 'src/utils/Common';
import { loadUrunControlDetail } from 'src/utils/storage/StartWorkOrder';
import logger from 'src/utils/logger';

const useStyles = makeStyles((theme) => ({
  root: {},
  dividerB: {
    backgroundColor: '#FF9800'
  },
  loaderContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3)
  },
  errorText: {
    color: theme.palette.error.main,
    textAlign: 'center',
    padding: theme.spacing(2)
  }
}));

function BulletinControl({ className }) {
  const classes = useStyles();
  const [bultenData, setBultenData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Kullanıcı ve Model Bilgileri
  const sessionUser = getUser();
  const birimKodu = sessionUser?.ServiceShopId;
  const teknisyenKodu = sessionUser?.TechnicianId;
  
  const dataDetail = loadUrunControlDetail();
  const modelKodu = dataDetail?.[0]?.KUL_MODEL_KODU || "-1";

  // Yetki Kontrolü (Memoized)
  const isKlimaTeknisyeni = React.useMemo(() => {
    const types = sessionUser?.TechnicianType?.split(",") || [];
    return types.some(type => parseInt(type) === 26);
  }, [sessionUser]);

  // Bültenleri Getir
  useEffect(() => {
    let isMounted = true;

    const fetchBulletins = async () => {
      try {
        setLoading(true);
        setError(null);
       
        const response = await  axios.get(`/GetArtemisBulten/${birimKodu}/${teknisyenKodu}/${safeRequestParam(modelKodu) || 'a'}`);
        
        if (isMounted) {
          setBultenData(Array.isArray(response.data) ? response.data : []);
        }
      } catch (err) {
        if (isMounted) {
          setError("Bülten verileri yüklenirken bir hata oluştu.");
          logger.error("Fetch Error:", err);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (!isKlimaTeknisyeni) {
      fetchBulletins();
    }

    return () => { isMounted = false; };
  }, [birimKodu, teknisyenKodu, modelKodu, isKlimaTeknisyeni]);

  // Bülten İndirme/Açma İşlemi
  const handleDownloadDetail = async (log) => {
    try {
      setLoading(true);
      const payload = {
        BIRIM_KODU: birimKodu?.toString(),
        SICIL_NO: sessionUser?.UserId,
        FilePtr: log.FP,
      };
      
      const config = {
        responseType: 'blob',
        headers: { 'Content-Type': 'application/json' }
      };

      const response = await axios.post('/FileImportExport/ArtemisBulten', payload, config);
      const fileName = `${log.BT}_${log.AY}_${log.BN}`;
      openBlob(response.data, fileName);
      
    } catch (err) {
      alert("Dosya indirilirken bir hata oluştu.");
      logger.error("Download Error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Yetki Engeli Ekranı
  if (isKlimaTeknisyeni) {
    return (
      <Card className={clsx(classes.root, className)}>
        <CardHeader title="Yetki Kontrolü" />
        <Divider />
        <CardContent>
          <Typography color="error">Yetkiniz bulunmadığı için bu sayfayı görüntüleyemezsiniz.</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={clsx(classes.root, className)}>
      <CardHeader title="Ürüne Ait Bültenler" />
      <Divider />
      <CardContent>
        {loading ? (
          <div className={classes.loaderContainer}>
            <CircularProgress color="primary" />
          </div>
        ) : error ? (
          <Typography className={classes.errorText}>{error}</Typography>
        ) : bultenData.length === 0 ? (
          <Alert severity="info">Gösterilecek bülten bulunamadı.</Alert>   
        ) : (
          <List>
            {bultenData.map((log) => (
              <React.Fragment key={log.BN}>
                <ListItem alignItems="flex-start">
                  <ListItemAvatar>
                    <div style={{ textAlign: 'center', minWidth: 50 }}>
                      <Typography variant="h4" color="textPrimary">{log.GUN}</Typography>
                      <Typography variant="caption" display="block" color="textSecondary">
                        {log.AY?.substring(0, 3).toUpperCase()}
                      </Typography>
                      <Typography variant="caption" display="block" color="textSecondary">
                        {log.YIL}
                      </Typography>
                    </div>
                  </ListItemAvatar>
                  <ListItemText
                    primary={log.BT}
                    secondary={
                      <Typography component="span" variant="body2" color="textSecondary">
                        {log.A}
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Bülten İndir">
                      <IconButton
                        color="primary"
                        edge="end"
                        onClick={() => handleDownloadDetail(log)}
                        disabled={loading}
                      >
                        <CloudDownloadIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
                <Divider className={classes.dividerB} />
              </React.Fragment>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
}

BulletinControl.propTypes = {
  className: PropTypes.string
};

export default BulletinControl;